import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { Gauge, GaugeDataFormat, GaugeOptions, GaugeDataFormatSerie } from '../../../charting/charts/Gauge/index';
import { SvgManager, Margins } from '../../..';
import { IDynamicCompileService } from '../../services/dynamicCompileService';
import { ITitle, Title, Legend } from '../../../charting';
import { RoundingHelper } from '../../../helpers/helpers';

class NewGaugeController implements angular.IComponentController,
    angular.IOnInit,
    angular.IOnChanges {

    data: GaugeDataFormat[];
    options: GaugeOptions;
    componentId: string;
    // boolean to indicate if options should be deep watched
    watchOptions: boolean;

    private _svgManager: SvgManager;
    private _container: d3.Selection<any>;
    private _gaugeContainer: d3.Selection<any>;
    private _legends: d3.Selection<any>;
    private _legendArray: Legend[];
    private _chart: Gauge;
    private _title: ITitle;
    private readonly _titlePadding = 10;
    private readonly _gaugePadding = 15;

    readonly ratio: number;

    static $inject = ['$scope', '$element', 'dynamicCompileService'];

    constructor(
        private _$scope: angular.IScope,
        private _$element: angular.IAugmentedJQuery,
        private _dynamicCompileService: IDynamicCompileService
    ) { }

    $onInit(): void {
        const id = this.componentId;
        if (!id) {
            console.error('Cannot use gauge. An id is required.');
        } else {
            this._$element.attr('id', id);

            // creating gauge(s)
            this._chart = new Gauge();

            // select the div below custom element
            this._svgManager = new SvgManager(id + ' div.gauge-container', new Margins(0, 0, 0, 0));
            this._container = this._svgManager.container();
            this._svgManager.addResizeFunction((w, h) => this.resizeComponent(w, h));

            // creating title
            this._title = new Title(this._container.append('g').classed('sm', true));
            this._title.ratio(this.ratio);
            this.updateTitle();

            // creating legends
            this._legends = this._container
                .append('g')
                .classed('legends-group', true);
            this.createLegendsFromData();

            // creating group for gauges
            this._gaugeContainer = this._container
                .append('g')
                .classed('gauges-container', true);

            this._chart
                .options(this.options)
                .data(this.data)
                .ratio(this.ratio || 1)
                .render(this._gaugeContainer);

            if (this.watchOptions) {
                this._$scope.$watch(() => this.options, () => {
                    this._chart
                        .options(this.options)
                        .render(this._gaugeContainer);
                }, true);
            }
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        this.updateTitle();
        this.updateLegends();

        // call resize if any display options has changed
        if (this._svgManager) {
            const size = this._svgManager.getSize();
            this.resizeComponent(size.width, size.height);
        }

        if (changesObj['data'] && this.data.filter(d => !!d.serie && !!d.serie.attributes).length) {
            const series = this.data.map(d => d.serie);
            this._dynamicCompileService.compileGroup(this._$element, this._$scope as any, null, series);
        }
        if (changesObj['ratio'] && this.ratio) {
            if (this._legendArray) {
                this._legendArray.forEach(l => l.ratio(this.ratio));
            }
            if (this._title) {
                this._title.ratio(this.ratio);
            }
        }
        if (this._chart) {
            this._chart
                .options(this.options)
                .data(this.data)
                .ratio(this.ratio || 1)
                .render(this._gaugeContainer);
        }
    }

    private createLegendsFromData() {
        this._legends.selectAll('.legend-group').remove();
        this._legendArray = this.data.map(df => {
            const l = this.createLegend(this._legends, df.serie, this.options.isLegendVisible);
            l.ratio(this.ratio || 1);
            return l;
        });
    }

    private createLegend(group: d3.Selection<any>, serie: GaugeDataFormatSerie, visible: boolean): Legend {
        const legend = new Legend(group);
        this.updateLegend(legend, serie, visible);
        return legend;
    }

    private resizeComponent(width: number, height: number) {
        let translateGauges = 0;
        const ratio = this.ratio || 1;
        // adapt height according to title visibility
        if (this.options.isTitleVisible) {
            const titlePadding = this._titlePadding * ratio;
            this._title.translate(width / 2, -titlePadding);
            translateGauges += titlePadding;
        }

        // realign each legend
        if (this.options.isLegendVisible) {
            const legendSpacing = (width / this.data.length);
            const legendPadding = this._gaugePadding * ratio;
            // align vertically all legends
            this._legends
                .attr({ 'transform': `translate(0, ${this.getTitleHeight() + legendPadding})` });

            // align horizontally each legends
            this._legendArray
                .forEach((l, i) => {
                    l.resize(width, height);
                    const ownWidth = RoundingHelper.notNaN(l.width(), 0);
                    // evenly distribute over existing gauge, centering depending on own width
                    const w = (legendSpacing / 2) + (legendSpacing * i) + (ownWidth / 2);
                    l.translate(w, 0);
                });
            const fixed = this.options.isTitleVisible ? legendPadding : 0;
            const legendHeight = this.getLegendsHeight();
            translateGauges += legendHeight + fixed;
        }
        // translate the whole gauges group to adapt according to title and legend
        this.translate(this._gaugeContainer, 0, translateGauges);
        const gaugeHeight = height - this.getTitleHeight() - this.getLegendsHeight();
        // display each gauges
        this._chart
            .width(width)
            .height(gaugeHeight)
            .ratio(this.ratio || 1)
            .render(this._gaugeContainer);
    }

    private updateLegend(legend: Legend, serie: GaugeDataFormatSerie, visible: boolean) {
        legend.isVisible(visible);
        legend.series(serie ? [{
            name: serie.label,
            color: () => serie.color,
            identifier: () => serie.id,
            legendText: () => serie.label,
            isVisible: true,
            legendAttributes: () => serie.attributes,
            click: serie.click
        } as any] : []);
        legend.ratio(this.ratio || 1);
    }

    private updateTitle() {
        if (this._title) {
            this._title.isVisible(!!this.options.isTitleVisible);
            this._title.text(this.options.title);
            this._title.ratio(this.ratio || 1);
        }
    }

    private updateLegends() {
        if (this._legendArray) {
            this.createLegendsFromData();
        }
    }

    private getTitleHeight() {
        const ratio = (this.ratio || 1);
        return this._title.isVisible()
            ? (this._title.height() + this._titlePadding) * ratio
            : 0;
    }

    private getLegendsHeight() {
        const ratio = (this.ratio || 1);
        const maxHeight = _.max(this._legendArray.map(l => l.height()));
        // maxHeight is already scaled according to ratio for some reason
        return _.some(this._legendArray, l => l.isVisible())
            ? maxHeight + (this._titlePadding) * ratio
            : 0;
    }

    private translate(group: d3.Selection<any>, translateX: number, translateY: number): void {
        translateX = isNaN(translateX) ? 0 : translateX;
        translateY = isNaN(translateY) ? 0 : translateY;

        group.transition()
            .attr({ 'transform': `translate(${translateX},${translateY})` });
    }
}

export const wtNewGauge: angular.IComponentOptions & { componentId: string } = {
    componentId: 'wtNewGauge',
    controller: NewGaugeController,
    template: '<div class="full-size gauge-container"><svg></svg></div>',
    controllerAs: 'vm',
    bindings: {
        componentId: '@',
        data: '<',
        options: '<',
        ratio: '<?',
        watchOptions: '<?',// boolean to indicate if options should be deep watched
    }
};